import { CircularProgress, FormControl, InputLabel, MenuItem } from '@mui/material';
import { format } from 'date-fns';
import { groupBy, uniqBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import useAsyncGuiHandler from '../../hooks/useAsyncHandler';
import usePresentations from '../../hooks/usePresentations';
import { Booth, Slot } from '../../interfaces/interfaces';
import { fetchAndSetSlots } from '../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../reduxToolkit/typedRedux';
import VySelect from '../common/VySelect';

interface PresentationSlotSelectProps {
  booth: Booth | null;
  id: string;
  onRefresh?: () => void;
}

interface SlotGroup {
  datetime_start: string;
  datetime_end: string;
}

const PresentationSlotSelect = (props: PresentationSlotSelectProps) => {
  const { booth: boothProp, onRefresh } = props;

  const booth = boothProp ?? useSelector((state) => state.admin.userbooth);
  const event = useSelector((state) => state.admin.event ?? state.common.event);
  const slots = useSelector((state) => state.common.slots);

  const dispatch = useDispatch();
  const { removeSlot, assignSlot, updateGroup } = usePresentations();
  const { t } = useTranslation(['account', 'common']);
  const [asyncHandler] = useAsyncGuiHandler();

  const presentation = booth?.presentationrooms?.find((presentationRoom) => presentationRoom._id === props.id);

  const [slotGroups, setSlotGroups] = useState<SlotGroup[]>([]);
  const [slot, setSlot] = useState<Slot>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const _groupedSlots = uniqBy(slots, (el) => [el.datetime_start, el.datetime_end].join());
    setSlotGroups(_groupedSlots.map((slot) => ({ datetime_start: slot.datetime_start, datetime_end: slot.datetime_end })));
    setSlot(slots.find((s) => s.meetingroom._id === presentation?._id));
  }, [slots]);

  async function handleSlotGroupChange(slotGroup: string) {
    setLoading(true);
    if (slotGroup === 'unassigned' && !!slot) await removeSlot(slot?._id);
    else {
      if (!!slot && Object.values(slot)?.join(' | ') === slotGroup) return;
      const datetime_start = slotGroup.substring(0, slotGroup.indexOf(' | '));
      const datetime_end = slotGroup.substring(slotGroup.indexOf(' | ') + 3, slotGroup.length);
      const groupedByDates = groupBy(slots, (el) => [el.datetime_start, el.datetime_end].join());
      const targetGroup = groupedByDates[datetime_start + ',' + datetime_end];
      const sortIndex = targetGroup.length;
      const newSlot: Slot = { _id: slot?._id ?? 'unassigned', meetingroom: presentation!, datetime_start, datetime_end, sortIndex };

      if (slot) await updateGroup([newSlot]);
      else await assignSlot(newSlot);
      onRefresh?.();
    }
    setLoading(false);
    dispatch(fetchAndSetSlots(event?._id));
  }

  return (
    <>
      {loading ? (
        <div className="grid w-full place-items-center">
          <CircularProgress />
        </div>
      ) : (
        <FormControl fullWidth sx={{ '& .MuiSelect-outlined': { color: 'white' }, '& .MuiInputLabel-root': { color: 'white' } }}>
          <InputLabel id="group-key-select-label">{t('account:slots.group')}</InputLabel>
          <VySelect
            labelId="group-key-select-label"
            id="group-key-select"
            value={slot ? Object.values({ datetime_start: slot?.datetime_start, datetime_end: slot?.datetime_end })?.join(' | ') : 'unassigned'}
            label={t('account:slots.group')}
            onChange={(e) => asyncHandler(handleSlotGroupChange(e.target.value as string))}>
            <MenuItem value={'unassigned'}>{t('account:slots.unassigned')}</MenuItem>
            {slotGroups.map((g) => (
              <MenuItem key={Object.values(g).join(' ')} value={Object.values(g)?.join(' | ')}>
                <div className="flex flex-row space-x-2">
                  <div>
                    <b>{format(new Date(g.datetime_start), 'dd.MM.yyyy')}</b>
                    &nbsp;&nbsp;
                    <span>{format(new Date(g.datetime_start), 'HH:mm')}</span>
                  </div>
                  <p>-</p>
                  <div>
                    <b>{format(new Date(g.datetime_end), 'dd.MM.yyyy')}</b>
                    &nbsp;&nbsp;
                    <span>{format(new Date(g.datetime_end), 'HH:mm')}</span>
                  </div>
                </div>
              </MenuItem>
            ))}
          </VySelect>
        </FormControl>
      )}
    </>
  );
};

export default PresentationSlotSelect;
